import { useMemo } from 'react';

import { Permission, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { Bonus } from '../../../../types/bonus';
import { UserMode } from '../../../../types/user';
import { isWithinEditWindow } from '../utils/bonus';

export const useContextualizedBonusDetails = (bonus: Bonus | null) => {
	const user = useCurrentUser();
	const canGiveRecognition = usePermission(Permission.CanGiveRecognition);

	return useMemo(() => {
		if (!bonus) {
			return {
				isCreatedByUser: false,
				isEditableByUser: false,
				isGiverHuman: false,
				isInitiatedByUser: false,
				isUserOnlyReceiver: false,
				isUserReceiver: false,
				isUserReceiversManager: false,
				canUserAddOn: false,
				onlyOneReceiver: false,
			};
		}

		const isUserReceiver = bonus.receivers.some(({ id }) => id === user.id);
		const isUserReceiversManager = bonus.receivers.some(({ id }) => user.direct_report_ids.includes(id));

		const isInitiatedByUser = bonus?.initiator?.id === user.id;
		const isCreatedByUser = isInitiatedByUser || bonus?.giver.id === user.id;
		const onlyOneReceiver = bonus.receivers.length === 1;
		const isUserOnlyReceiver = isUserReceiver && onlyOneReceiver;
		const canUserAddOn = !isUserOnlyReceiver && canGiveRecognition;
		const isGiverHuman = bonus.giver.user_mode === UserMode.Normal || bonus.giver.user_mode === UserMode.Benefactor;
		const isEditableByUser = isCreatedByUser && bonus.editable && isWithinEditWindow(bonus) && canGiveRecognition;

		return {
			isCreatedByUser,
			isEditableByUser,
			isGiverHuman,
			isInitiatedByUser,
			isUserOnlyReceiver,
			isUserReceiver,
			isUserReceiversManager,
			canUserAddOn,
			onlyOneReceiver,
		};
	}, [bonus, canGiveRecognition, user]);
};
